import StatementPageTypeLink from "@gerrieDev/Components/StatementPageTypeLink";
import { StatementDataViewContext } from "@gerrieDev/Store/Data/Statements/StatementSelector";
import updateSearchFields from "@gerrieDev/Store/Pages/Statements/Handlers/UpdateSearchFields";
import { CustomState } from "@gerrieDev/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showStatementNumber = "showStatementNumber",
    showStatementDate = "showStatementDate",
    showPreviousBalance = "showPreviousBalance",
    showPaymentReceived = "showPaymentReceived",
    showPastDue = "showPastDue",
    showNewCharges = "showNewCharges",
    showBalance = "showBalance",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showStatementNumber]: boolean;
        [fields.showStatementDate]: boolean;
        [fields.showPreviousBalance]: boolean;
        [fields.showPaymentReceived]: boolean;
        [fields.showPastDue]: boolean;
        [fields.showNewCharges]: boolean;
        [fields.showBalance]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => ({
    getStatementsParameter: state.customPagesReducer.statements.getStatementsParameter,
    failedToLoadStatements: state.statementReducer.failedToLoadStatements,
    language: state.context.session.language,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StatementTableStyles {
    container?: InjectableCss;
    headerClickables?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    statementNumberHeader?: DataTableHeaderProps;
    statementDateHeader?: DataTableHeaderProps;
    previousBalanceHeader?: DataTableHeaderProps;
    paymentReceivedHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    pastDueHeader?: DataTableHeaderProps;
    NewChargesHeader?: DataTableHeaderProps;
    balanceHeader?: DataTableHeaderProps;
    buttonHeader?: DataTableHeaderProps;
    statementNumberCell?: DataTableCellProps;
    statementDateCell?: DataTableCellProps;
    previousBalanceCell?: DataTableCellProps;
    paymentReceivedCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    pastDueCell?: DataTableCellProps;
    NewChargesCell?: DataTableCellProps;
    balanceCell?: DataTableCellProps;
    buttonCell?: DataTableCellProps;
}

const defaultHeaderProps = {
    sortClickableProps: {
        css: css`
            @media print {
                display: flex !important;
            }
        `,
    },
    sortIconProps: {
        css: css`
            @media print {
                display: none !important;
            }
        `,
    },
    typographyProps: {
        css: css`
            @media print {
                font-size: 11px !important;
            }
        `,
    },
};

const defaultCellProps = {
    css: css`
        @media print {
            text-align: left;
        }
    `,
    typographyProps: {
        css: css`
            @media print {
                font-size: 11px !important;
            }
        `,
    },
}

export const statementTableStyles: StatementTableStyles = {
    container: {
        css: css`
            overflow: auto;
            @media print {
                overflow: hidden;
                max-width: 100% !important;
            }
        `,
    },
    dataTable: {
        css: css`
            @media print {
                display: table !important;
                max-width: 100% !important;
                table-layout: fixed !important;
            }
        `,
    },
    dataTableHead: {
        css: css`
            @media print {
                display: table-head !important;
            }
        `,
    },
    dataTableBody: {
        css: css`
            @media print {
                display: table-row-group !important;
            }
        `,
    },
    dataTableRow: {
        css: css`
            @media print {
                width: 100% !important;
                display: table-row !important;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    headerClickables: {
        css: css`
            &:focus {
                outline-style: none;
            }
            width: 100%;
            justify-content: space-between;
            display: block;
        `, // TODO ISC-12604 focus removal is incorrect
    },
    buttonHeader: {
        alignX: "right",
        css: css`
            @media print {
                display: none !important;
            }
        `,
    },
    statementNumberCell: defaultCellProps,
    statementDateCell: defaultCellProps,
    previousBalanceCell: defaultCellProps,
    paymentReceivedCell: defaultCellProps,
    shipToCell: defaultCellProps,
    pastDueCell: defaultCellProps,
    NewChargesCell: defaultCellProps,
    balanceCell: {
        alignX: "right",
        ...defaultCellProps
    },
    buttonCell: {
        alignX: "right",
        css: css`
            @media print {
                display: none !important;
            }
        `,
    },
    balanceHeader: {
        alignX: "right",
        ...defaultHeaderProps,
    },
    statementDateHeader: defaultHeaderProps,
    statementNumberHeader: defaultHeaderProps,
    previousBalanceHeader: {
        sortClickableProps: {
            css: css`
                @media print {
                    display: flex !important;
                    max-width: 77px !important;
                    white-space: normal;
                }
            `,
        },
        typographyProps: {
            ...defaultHeaderProps.typographyProps
        },
        sortIconProps: {
            ...defaultHeaderProps.sortIconProps
        },
    },
    paymentReceivedHeader: {
        sortClickableProps: {
            css: css`
                @media print {
                    display: flex !important;
                    max-width: 77px !important;
                    white-space: normal;
                }
            `,
        },
        typographyProps: {
            ...defaultHeaderProps.typographyProps
        },
        sortIconProps: {
            ...defaultHeaderProps.sortIconProps
        },
    },
    pastDueHeader: defaultHeaderProps,
    NewChargesHeader: defaultHeaderProps,
};

const styles = statementTableStyles;

const StatementTable = (props: Props) => {
    const headerClick = (sortField: string) => {
        const sort = props.getStatementsParameter.sort === sortField ? `${sortField} DESC` : sortField;
        props.updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (props.getStatementsParameter.sort === sortField) {
            sorted = "ascending";
        } else if (props.getStatementsParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    if (props.failedToLoadStatements) {
        return null;
    }

    const statementsDataView = useContext(StatementDataViewContext);
    if (!statementsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (statementsDataView.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No Statements found")}</Typography>
            </StyledWrapper>
        );
    }

    const rows = statementsDataView.value.map(statement => {
        return {
            id: statement.id,
            statementNumber: statement.statementNumber,
            date: statement.statementDate,
            previousBalance: statement.previousBalanceDisplay,
            paymentReceived: statement.paymentReceivedDisplay,
            pastDue: statement.pastDueDisplay,
            newCharges: statement.newChargesDisplay,
            balance: statement.balanceDisplay,
        };
    });

    const {
        showStatementNumber,
        showStatementDate,
        showPreviousBalance,
        showPaymentReceived,
        showPastDue,
        showNewCharges,
        showBalance,
    } = props.fields;

    const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

    const history = useHistory();
    const detailsClickHandler = (statementNumber: string) => {
        const url = `/MyAccount/Statements/Details?statementNumber=${statementNumber}`;
        history.push(url);
    };

    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    {isTrueOrUndefined(showStatementDate) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("statementDate")}
                            {...styles.statementDateHeader}
                            onSortClick={() => headerClick("statementDate")}
                            data-test-selector="statement_statementHeader_date"
                        >
                            {translate("Date")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showStatementNumber) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("statementNumber")}
                            {...styles.statementNumberHeader}
                            onSortClick={() => headerClick("statementNumber")}
                        >
                            {translate("Statement")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showPreviousBalance) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("previousBalance")}
                            {...styles.previousBalanceHeader}
                            onSortClick={() => headerClick("previousBalance")}
                        >
                            {translate("Previous Balance")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showPaymentReceived) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("paymentReceived")}
                            {...styles.paymentReceivedHeader}
                            onSortClick={() => headerClick("paymentReceived")}
                        >
                            {translate("Payment Received")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showPastDue) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("pastDue")}
                            {...styles.pastDueHeader}
                            onSortClick={() => headerClick("pastDue")}
                        >
                            {translate("Past Due")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showNewCharges) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("newCharges")}
                            {...styles.NewChargesHeader}
                            onSortClick={() => headerClick("NewCharges")}
                        >
                            {translate("New Charges")}
                        </DataTableHeader>
                    )}
                    {isTrueOrUndefined(showBalance) && (
                        <DataTableHeader
                            tight
                            sorted={sorted("balance")}
                            {...styles.balanceHeader}
                            onSortClick={() => headerClick("balance")}
                        >
                            {translate("Balance")}
                        </DataTableHeader>
                    )}
                    <DataTableHeader {...styles.buttonHeader}>
                        {translate("")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        ({
                            id,
                            statementNumber,
                            date,
                            previousBalance,
                            paymentReceived,
                            pastDue,
                            newCharges,
                            balance,
                        }) => (
                            <DataTableRow
                                key={id}
                                {...styles.dataTableRow}
                                data-test-selector="statement_statementLine"
                            >
                                {isTrueOrUndefined(showStatementDate) && (
                                    <DataTableCell
                                        {...styles.statementDateCell}
                                        data-test-selector="statement_statementLine_date"
                                    >
                                        <LocalizedDateTime
                                            dateTime={date}
                                            options={{month:'2-digit',day:'2-digit', year:'numeric'}}
                                        />
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showStatementNumber) && (
                                    <DataTableCell
                                        {...styles.statementNumberCell}
                                        data-test-selector="statement_statementLine_number"
                                    >
                                        <StatementPageTypeLink
                                            title={statementNumber}
                                            statementNumber={statementNumber}
                                        />
                                    </DataTableCell>
                                )}
                                {isTrueOrUndefined(showPreviousBalance) && (
                                    <DataTableCell {...styles.previousBalanceCell}>{previousBalance}</DataTableCell>
                                )}
                                {isTrueOrUndefined(showPaymentReceived) && (
                                    <DataTableCell {...styles.paymentReceivedCell}>{paymentReceived}</DataTableCell>
                                )}
                                {isTrueOrUndefined(showPastDue) && (
                                    <DataTableCell {...styles.pastDueCell}>{pastDue}</DataTableCell>
                                )}
                                {isTrueOrUndefined(showNewCharges) && (
                                    <DataTableCell {...styles.NewChargesCell}>{newCharges}</DataTableCell>
                                )}
                                {isTrueOrUndefined(showBalance) && (
                                    <DataTableCell {...styles.balanceCell}>{balance}</DataTableCell>
                                )}
                                <DataTableCell {...styles.buttonCell}>
                                    <Button onClick={() => detailsClickHandler(statementNumber)}>{translate("Details")}</Button>
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(StatementTable),
    definition: {
        group: "Basic",
        displayName: "Statement Table",
        fieldDefinitions: [
            {
                name: fields.showStatementNumber,
                displayName: "Statement #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.showStatementDate,
                displayName: "Statement Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showPreviousBalance,
                displayName: "Previous Balance",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showPaymentReceived,
                displayName: "Payment Received",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPastDue,
                displayName: "Past Due",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showNewCharges,
                displayName: "New Changes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showBalance,
                displayName: "Balance",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
